import React from 'react';
import type {SerializedStyles} from '@sail/engine/deprecated';
// eslint-disable-next-line no-restricted-imports
import {Banner, view} from '@sail/ui';
import {InlineFeedback} from '@sail/ui/beta';
import type {
  StripeCardExpiryElement,
  StripeCardNumberElement,
  Stripe as StripeJS,
  StripeCardCvcElementChangeEvent,
  StripeCardExpiryElementChangeEvent,
  StripeCardNumberElementChangeEvent,
} from '@stripe/stripe-js';
import {message, useIntl} from '@sail/intl';
import {
  expressDebitCardSupportMap,
  expressDebitCardBetaSupportMap,
} from '@dashboard/data/ts/instant_payouts';
import type {MessageDescriptor} from '@sail/intl';
import {useAnalytics} from '@sail/observability';
import type {V2AccountsVTwoCountry} from '@stripe-internal/data-v2/schema/types';
import {formatLast4WithMaxDots} from '../../utils/payments';
import {Button} from '../../SharedComponents/Button/Button';
import type {IMerchantDataContext} from '../../context/merchant/MerchantDataContext';
import {useMerchantData} from '../../context/merchant/MerchantDataContext';
import {useSoftAssertFailed, useFirmAssertFailed} from '../../context';
import * as utils from './utils';
import {DebitCardFormIframe} from './DebitCardFormIframe';

// Referenced code from manage/frontend/connect_lib/src/components/accounts/formCompositions/externalAccount/DebitCardForm.tsx

// eslint-disable-next-line @stripe-internal/stripe/no-mixed-react-exports
export const messages = {
  cardNumLabel: message({
    id: 'connectEmbedLib.debitCardForm.cardNumLabel',
    description: 'Label for the debit card number field',
    defaultMessage: 'Card number',
  }),

  cardExpDateLabel: message({
    id: 'connectEmbedLib.debitCardForm.cardExpDateLabel',
    description: 'Label for the debit card expiration date field',
    defaultMessage: 'Expiration date',
  }),

  cardCVCLabel: message({
    id: 'connectEmbedLib.debitCardForm.cardCVCLabel',
    description: 'Label for the debit card CVC field',
    defaultMessage: 'Security code',
  }),

  currencyLabel: message({
    id: 'connectEmbedLib.debitCardForm.currencyLabel',
    description: 'Label for the currency field',
    defaultMessage: 'Currency',
  }),

  genericError: message({
    id: 'connectEmbedLib.debitCardForm.genericError',
    description: 'A generic error after submitting the debit card',
    defaultMessage: 'An error has occurred.',
  }),

  cardOutboundSupportabilityError: message({
    id: 'connectEmbedLib.debitCardForm.cardOutboundSupportabilityError',
    description: 'The submitted card cannot accept payouts',
    defaultMessage:
      'This card is not eligible to receive payouts. Please use another debit card.',
  }),
};

const CUSTOM_ERROR_CODE_MESSAGES: Record<string, MessageDescriptor> = {
  card_outbound_supportability_invalid:
    messages.cardOutboundSupportabilityError,
};

export type ExistingCard = {
  object: 'card';
  last4: string;
  /* eslint-disable camelcase */
  exp_month: number;
  exp_year: number;
  /* eslint-enable camelcase */
};

type DefaultButtonProps = {
  type?: 'default';
  disabled?: boolean;
  onSuccess: ({token, currency}: {token: string; currency: string}) => void;
  style?: SerializedStyles;
  text: string;
};

type CustomButtonProps = {
  type: 'custom';
  onSuccess: ({token, currency}: {token: string; currency: string}) => void;
  renderCustomButton: (
    handleSubmit: () => Promise<void>,
    submitDisabled: boolean,
    loading: boolean,
  ) => React.ReactNode;
};

type V2CardParams = {
  billing_details: {
    name: string | null;
    address: {
      city: string | null;
      country: V2AccountsVTwoCountry | null;
      line1: string | null;
      line2: string | null;
      postal_code: string | null;
      state: string | null;
    };
  };
  preconditions: {
    outbound_supported: boolean;
  };
};

type V2Params = {
  V2CardParams: V2CardParams;
  stripeJsV2Config: utils.StripeJsV2Config;
  onSuccess: (v2PaymentMethod: V2PaymentMethod) => void;
};

export type V2PaymentMethod = {
  id: string;
  object: string;
  billing_details: {
    address: {
      city: string | null;
      country: V2AccountsVTwoCountry;
      line1: string | null;
      line2: string | null;
      postal_code: string | null;
      state: string | null;
    };
    name: string;
  };
  created: string;
  exp_month: string;
  exp_year: string;
  last4: string;
};

// NOTE(hughxie): Typings are not supported for the internal version of stripe.js required for V2.
interface V2StripeJS extends StripeJS {
  createV2CardPaymentMethod: (params: {
    element: StripeCardNumberElement;
    stripeContext: string;
    authToken: string;
    params: V2CardParams;
  }) => Promise<{
    error?: {code: string; user_message: string};
    paymentMethod?: V2PaymentMethod;
  }>;
}

type Props = {
  /** Button props to style the "Submit" button, including an onSuccess callback */
  buttonProps: DefaultButtonProps | CustomButtonProps;
  /** Country of the debit card */
  country: string;
  /** Boolean indicating whether the form is disabled */
  disabled?: boolean;
  /** An existing card object that could populate the form */
  existingCard?: ExistingCard | null | undefined;
  /** The header of the form given the values of the bank account form */
  header?: React.ReactNode;
  /** The footer of the form given the values of the bank account form */
  footer?: React.ReactNode;
  /** Renders additional content given the values of the bank account form */
  renderContent?: () => React.ReactNode;
  /** Alternative label for card number field */
  label?: MessageDescriptor<never>;
  /** Error that occured outside the DebitCardForm that should be displayed */
  usageError?: string;
  /** Renders a banner in test mode that allows the user to automatically fill in debit card values. */
  renderTestModeFill?: (onSubmit: () => void) => React.ReactNode;
  /** Is the submit step loading */
  loading?: boolean;
  /** Identifier for analytics events. Using type string rather than ExternalAccountFormClientIdentifier out of laziness */
  clientIdentifier: string;
  /** Any content you want directly above the submit button */
  contentAboveSubmit?: JSX.Element | null;
  /** V2 config used to create vNext cards with a private StripeJs release */
  V2Params?: V2Params;
  /** Callback triggered when form is loaded */
  onFormLoaded?: () => void;
};

const DebitCardInner = ({
  buttonProps,
  disabled,
  header,
  existingCard,
  country,
  footer,
  renderContent,
  label,
  usageError,
  renderTestModeFill,
  merchantData,
  loading: submitLoading,
  clientIdentifier,
  contentAboveSubmit,
  V2Params,
  onFormLoaded,
}: {
  merchantData: IMerchantDataContext;
} & Props) => {
  const analytics = useAnalytics();

  React.useEffect(() => {
    analytics.viewed('debit_card_form.viewed', {
      external_account_form_use_case: clientIdentifier || 'unknown',
    });
    // Only run on first render
    // eslint-disable-next-line @stripe-internal/stripe/exhaustive-deps, react-hooks/exhaustive-deps
  }, []);

  const intl = useIntl();

  const {currency, test_token: testToken} =
    (expressDebitCardSupportMap[country.toUpperCase()] ||
      expressDebitCardBetaSupportMap[country.toUpperCase()]) ??
    {};

  const softAssertFailed = useSoftAssertFailed();
  const firmAssertFailed = useFirmAssertFailed();

  const [stripe, setStripe] = React.useState<StripeJS | null>(null);
  const [expiryStatus, setExpiryFieldStatus] =
    React.useState<StripeCardExpiryElementChangeEvent | null>(null);
  const [cardStatus, setCardFieldStatus] =
    React.useState<StripeCardNumberElementChangeEvent | null>(null);
  const [cvcStatus, setCvcFieldStatus] =
    React.useState<StripeCardCvcElementChangeEvent | null>(null);

  const [cardElement, setCardElement] =
    React.useState<StripeCardNumberElement | null>(null);
  const [expiryElement, setExpiryElement] =
    React.useState<StripeCardExpiryElement | null>(null);

  const [error, setError] = React.useState<string | React.ReactNode | null>(
    null,
  );

  const [createTokenLoading, setCreateTokenLoading] = React.useState(true);
  const loading = createTokenLoading || !!submitLoading;

  const handleSubmit = async () => {
    if (loading) {
      return;
    }

    setCreateTokenLoading(true);
    setError(null);

    if (!stripe || !cardElement) {
      softAssertFailed(
        'DebitCardForm: handleSubmit called in non-loading state, but stripe or cardElement dont exist',
        'linked_external_account_form',
      );
      return;
    }

    if (V2Params?.stripeJsV2Config && V2Params.V2CardParams) {
      const {error, paymentMethod} = await (
        stripe as V2StripeJS
      ).createV2CardPaymentMethod({
        element: cardElement,
        stripeContext: V2Params.stripeJsV2Config.__v2PrivateBeta.stripeContext,
        authToken: V2Params.stripeJsV2Config.__v2PrivateBeta.authToken,
        params: V2Params.V2CardParams,
      });

      setCreateTokenLoading(false);

      if (error) {
        if (error.code && CUSTOM_ERROR_CODE_MESSAGES[error.code]) {
          setError(intl.formatMessage(CUSTOM_ERROR_CODE_MESSAGES[error.code]));
        } else {
          setError(error.user_message);
          firmAssertFailed(
            'DebitCardForm: V2 card creation failed',
            'recipient_payouts',
            {
              error_code: error.code,
              error_message: error.user_message,
              recipientId:
                V2Params.stripeJsV2Config.__v2PrivateBeta.stripeContext,
              raw_error: JSON.stringify(error),
            },
          );
        }
      }

      if (paymentMethod) {
        setError(null);
        V2Params.onSuccess(paymentMethod);
      }

      return;
    }

    const response = await stripe.createToken(cardElement, {currency});

    setCreateTokenLoading(false);

    if (response.error) {
      setError(
        response.error.message ?? intl.formatMessage(messages.genericError),
      );
      return;
    }

    if (response.token) {
      const currency = response.token.card?.currency;

      if (!currency) {
        softAssertFailed(
          'DebitCardForm: token created successfully, but currency was not returned. Not proceeding to set external account',
          'linked_external_account_form',
          {
            token: response.token.id,
            currency: currency ?? '',
          },
        );

        setError(intl.formatMessage(messages.genericError));
        return;
      }

      buttonProps.onSuccess({
        token: response.token.id,
        currency,
      });
    }
  };

  const isButtonDisabled =
    loading ||
    !cardStatus?.complete ||
    !expiryStatus?.complete ||
    (buttonProps as DefaultButtonProps).disabled ||
    disabled ||
    (V2Params && !cvcStatus?.complete);

  const initializeOverrideFields = () => {
    if (!existingCard) {
      return {number: undefined, expiry: undefined};
    }

    return {
      number: existingCard.last4 && formatLast4WithMaxDots(existingCard.last4),
      expiry: utils.getFormattedExpiry(existingCard),
    };
  };

  const [{number: overrideNumber, expiry: overrideExpiry}, setOverrideFields] =
    React.useState<{number?: string; expiry?: string}>(
      initializeOverrideFields(),
    );

  const clearOverrideFields = () =>
    setOverrideFields({number: undefined, expiry: undefined});

  const onTestModeFill = async () => {
    setOverrideFields({
      number: '•••• •••• •••• ••••',
      expiry: '•• / ••',
    });
    buttonProps.onSuccess({token: testToken, currency});
  };

  if (!currency || !testToken) {
    softAssertFailed(
      'DebitCardForm rendered with invalid currency or testToken',
      'linked_external_account_form',
      {
        currency,
        testToken,
      },
    );

    return null;
  }

  /** We use an iframe here because stripe-js doesn't support the shadow DOM
    (tracked in https://github.com/stripe-archive/react-stripe-elements/issues/216).
    See more about the design at https://go/embedded-debit-card-design. */
  return (
    <view.div css={{stack: 'y', gapY: 'space.150'}}>
      {usageError && <Banner description={usageError} type="critical" />}
      {header}
      {renderContent && renderContent()}
      {!merchantData.livemode &&
        renderTestModeFill &&
        renderTestModeFill(onTestModeFill)}
      {error && (
        <InlineFeedback type="error">
          <view.span css={{font: 'body.medium'}}>{error}</view.span>
        </InlineFeedback>
      )}

      <DebitCardFormIframe
        cardElement={cardElement}
        expiryElement={expiryElement}
        cardStatus={cardStatus}
        expiryStatus={expiryStatus}
        cvcStatus={cvcStatus}
        label={label}
        loading={loading}
        merchantData={merchantData}
        overrideNumber={overrideNumber}
        overrideExpiry={overrideExpiry}
        clearOverrideFields={clearOverrideFields}
        handleError={(error: string) => setError(error)}
        handleStripeJS={(stripe: StripeJS) => setStripe(stripe)}
        handleCardElement={(cardElement: StripeCardNumberElement) =>
          setCardElement(cardElement)
        }
        handleExpiryElement={(expiryElement: StripeCardExpiryElement) =>
          setExpiryElement(expiryElement)
        }
        handleCardFieldStatus={(status: StripeCardNumberElementChangeEvent) =>
          setCardFieldStatus(status)
        }
        handleExpiryFieldStatus={(status: StripeCardExpiryElementChangeEvent) =>
          setExpiryFieldStatus(status)
        }
        handleCvcFieldStatus={(status: StripeCardCvcElementChangeEvent) =>
          setCvcFieldStatus(status)
        }
        handleTokenLoading={(loading: boolean) =>
          setCreateTokenLoading(loading)
        }
        stripeJs={stripe}
        stripeJsV2Config={V2Params?.stripeJsV2Config}
        onFormLoaded={onFormLoaded}
      />
      {contentAboveSubmit}
      {buttonProps.type !== 'custom' && (
        <Button
          uses={buttonProps.style ? [buttonProps.style] : []}
          type="primary"
          disabled={isButtonDisabled}
          onPress={handleSubmit}
          pending={loading}
        >
          {buttonProps.text}
        </Button>
      )}
      {buttonProps.type === 'custom' &&
        (buttonProps as CustomButtonProps).renderCustomButton(
          handleSubmit,
          !!isButtonDisabled,
          loading,
        )}
      {footer}
    </view.div>
  );
};

export const DebitCardForm = (props: Props): JSX.Element | null => {
  const merchantData = useMerchantData();
  if (!merchantData) {
    return null;
  }

  return <DebitCardInner merchantData={merchantData} {...props} />;
};
